import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useLanguage } from '../hooks/useLanguage';
import { useActiveSection } from '../hooks/useReveal';
import { content } from '../data/content';
import { Logo } from './ui/Logo';
import { Icon } from './ui/Icon';
import { Button } from './ui/Button';
import { ThemeToggle, LanguageToggle } from './ui/Toggle';

const NAV_IDS = ['home', 'services', 'process', 'work', 'pricing', 'faq', 'contact'];

export const Header = () => {
  const { language } = useLanguage();
  const t = content[language];
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const active = useActiveSection(NAV_IDS);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const navItems = [
    { id: 'services', label: t.nav.services },
    { id: 'process',  label: t.nav.process },
    { id: 'work',     label: t.nav.work },
    { id: 'pricing',  label: t.nav.pricing },
    { id: 'faq',      label: t.nav.faq },
  ];

  const goTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setOpen(false);
  };

  return (
    <>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 z-[150] btn-primary"
        onClick={(e) => { e.preventDefault(); goTo('home'); }}
      >
        {t.nav.skipToContent}
      </a>

      <header
        className={`fixed top-0 left-0 right-0 z-[110] transition-all duration-300 ease-smooth ${
          scrolled ? 'py-2.5' : 'py-4'
        }`}
      >
        <div className="container">
          <div
            className={`flex items-center justify-between gap-4 rounded-full border transition-all duration-300 ease-smooth ${
              scrolled ? 'glass shadow-soft px-3 sm:px-4 py-2' : 'border-transparent px-3 sm:px-4 py-2'
            }`}
            style={{ borderColor: scrolled ? 'var(--border)' : 'transparent' }}
          >
            <button
              type="button"
              onClick={() => goTo('home')}
              className="flex items-center"
              aria-label="EMFA — Home"
            >
              <Logo className="h-16 md:h-20" />
            </button>

            <nav className="hidden md:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
              {navItems.map((item) => {
                const isActive = active === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => goTo(item.id)}
                    className={`relative px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${
                      isActive ? 'text-app' : 'text-muted hover:text-app'
                    }`}
                  >
                    {isActive && (
                      <motion.span
                        layoutId="nav-pill"
                        className="absolute inset-0 rounded-full"
                        style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)' }}
                        transition={{ type: 'spring', stiffness: 360, damping: 30 }}
                      />
                    )}
                    <span className="relative">{item.label}</span>
                  </button>
                );
              })}
            </nav>

            <div className="flex items-center gap-2">
              <div className="hidden md:flex items-center gap-2">
                <LanguageToggle />
                <ThemeToggle />
              </div>
              <Button
                size="sm"
                variant="primary"
                onClick={() => goTo('contact')}
                icon={<Icon name="arrowUpRight" size={14} />}
                className="hidden sm:inline-flex"
              >
                {t.nav.consult}
              </Button>
              <button
                type="button"
                className="md:hidden inline-flex items-center justify-center h-9 w-9 rounded-full border"
                style={{ borderColor: 'var(--border-strong)' }}
                aria-label={open ? t.nav.close : t.nav.menu}
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
              >
                <AnimatePresence mode="wait" initial={false}>
                  <motion.span
                    key={open ? 'close' : 'open'}
                    initial={{ opacity: 0, rotate: -45 }}
                    animate={{ opacity: 1, rotate: 0 }}
                    exit={{ opacity: 0, rotate: 45 }}
                    transition={{ duration: 0.18 }}
                  >
                    <Icon name={open ? 'close' : 'menu'} size={18} />
                  </motion.span>
                </AnimatePresence>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[105] md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-ink-950/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ y: '-100%' }}
              animate={{ y: 0 }}
              exit={{ y: '-100%' }}
              transition={{ type: 'spring', stiffness: 380, damping: 40 }}
              className="absolute top-0 left-0 right-0 bg-surface border-b shadow-lift"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="container pt-24 pb-8 sm:py-20 sm:pb-10">
                <nav className="flex flex-col">
                  {navItems.map((item, idx) => (
                    <motion.button
                      key={item.id}
                      type="button"
                      onClick={() => goTo(item.id)}
                      initial={{ opacity: 0, x: -16 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.05 * idx, ease: [0.16, 1, 0.3, 1] }}
                      className="flex items-center justify-between py-3 text-display text-lg sm:text-2xl font-semibold text-app border-b group"
                      style={{ borderColor: 'var(--border)' }}
                    >
                      <span>{item.label}</span>
                      <Icon name="arrowUpRight" className="text-subtle group-hover:text-app transition-colors" />
                    </motion.button>
                  ))}
                </nav>
                <div className="flex items-center justify-between mt-8">
                  <div className="flex items-center gap-2">
                    <LanguageToggle />
                    <ThemeToggle />
                  </div>
                  <Button onClick={() => goTo('contact')} icon={<Icon name="arrowRight" size={14} />}>
                    {t.nav.consult}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
